import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAuthContext } from '../UserAuthContext';

const ResetPasswordForm = () => {
  const { user } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [token, setToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Extract token from URL and redirect if missing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fragmentParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const tokenFromUrl = fragmentParams.get('token') || params.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      window.history.replaceState(null, document.title, window.location.pathname);
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Your password has been reset successfully. You can now go back home.');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Fetch error:', err); // Debugging log
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .reset-password-wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background-color: #f4f4f4;
            padding: 20px;
          }
          .reset-password-container {
            max-width: 400px;
            width: 100%;
            padding: 20px;
            background-color: #fff;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
          .reset-password-title {
            text-align: center;
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 10px;
          }
          .reset-password-description {
            text-align: center;
            font-size: 14px;
            color: #666;
            margin-bottom: 20px;
          }
          .reset-password-input-container {
            margin-bottom: 20px;
          }
          .reset-password-input {
            width: 100%;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
            box-sizing: border-box;
          }
          .reset-password-input:focus {
            outline: none;
            border-color: #4a90e2;
            box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
          }
          .reset-password-message {
            text-align: center;
            font-size: 14px;
            margin-bottom: 20px;
          }
          .reset-password-success {
            color: #2e7d32;
          }
          .reset-password-error {
            color: #d32f2f;
          }
          .reset-password-button {
            width: 100%;
            padding: 10px;
            background-color: #4a90e2;
            color: #fff;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
          }
          .reset-password-button:hover:not(:disabled) {
            background-color: #357abd;
          }
          .reset-password-button:disabled {
            background-color: #90caf9;
            cursor: not-allowed;
          }
          .reset-password-back-link {
            text-align: center;
            margin-top: 20px;
          }
          .reset-password-back-link a {
            font-size: 14px;
            color: #4a90e2;
            text-decoration: none;
          }
          .reset-password-back-link a:hover {
            color: #357abd;
            text-decoration: underline;
          }
        `}
      </style>
      <div className="reset-password-wrapper">
        <div className="reset-password-container">
          {user ? (
            <>
              <h2 className="reset-password-title">Session Active</h2>
              <p className="reset-password-message reset-password-error">
                Log out to reset your password!
              </p>
              <div className="reset-password-back-link">
                <a href="/">Back Home</a>
              </div>
            </>
          ) : message ? (
            <>
              <h2 className="reset-password-title">Password Reset</h2>
              <p className="reset-password-message reset-password-success">
                {message}
              </p>
              <div className="reset-password-back-link">
                <a href="/">Go to Home</a>
              </div>
            </>
          ) : (
            <>
              <h2 className="reset-password-title">Reset Your Password</h2>
              <p className="reset-password-description">
                Enter your new password below.
              </p>
              {error && <div className="reset-password-message reset-password-error">{error}</div>}
              <div className="reset-password-input-container">
                <label htmlFor="new-password" style={{ display: 'none' }}>New Password</label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="reset-password-input"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="reset-password-input-container">
                <label htmlFor="confirm-password" style={{ display: 'none' }}>Confirm Password</label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="reset-password-input"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <button
                className="reset-password-button"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
              <div className="reset-password-back-link">
                <a href="/">Back Home</a>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPasswordForm;
